/**
 * The subset of SVG a vendored Twemoji file is allowed to reduce to.
 *
 * This module is the contract between two parties that must not drift apart:
 * `scripts/build-poem-art.ts`, which rejects any input element or attribute not
 * named here, and `twemoji-manifest.test.ts`, which re-runs the same refusal over
 * the committed artwork. Widening the whitelist is therefore a deliberate act in
 * one place rather than something that happens by accident when upstream redraws
 * an emoji.
 *
 * WHY A WHITELIST AT ALL, NOW THAT NOTHING IS INLINED. Until phase 0018 the
 * answer was direct: the geometry became React elements, so an attribute that got
 * through was an attribute on a rendered page. The artwork is served as files and
 * referenced by `<img>` now, and an SVG loaded that way renders in the
 * specification's **secure static mode** — script never executes, external
 * references never load, document CSS never applies. The browser enforces what
 * this file used to be the only guard against.
 *
 * It is kept because it proves something the browser does not: that the bytes we
 * vendored are plain geometry and paint, unmodified from upstream, which is the
 * claim the site makes in its footer under CC-BY 4.0. The elements and attributes
 * below are the ones that claim admits. A `<style>` block, a `style` attribute,
 * `<script>`, `<image href>`, `<use xlink:href>` or SMIL `<animate>` arriving in
 * a refreshed icon stops the build, where otherwise it would be copied into
 * `public/` verbatim and never looked inside again.
 */

export const TWEMOJI_TAGS = Object.freeze([
  'circle',
  'ellipse',
  'line',
  'path',
  'polygon',
  'polyline',
  'rect',
] as const)

export type TwemojiTag = (typeof TWEMOJI_TAGS)[number]

/**
 * Attribute names in their React spelling — `stroke-width` arrives as
 * `strokeWidth`, because `twemoji-parse.ts` normalises on the way in and has
 * done since the geometry was React elements. The spelling is kept rather than
 * reverted: it is what the parser emits, it is what its own tests assert, and
 * changing it would be churn in a module whose whole value is that it does not
 * drift.
 */
export const TWEMOJI_ATTRS = Object.freeze([
  'cx',
  'cy',
  'd',
  'fill',
  'height',
  'opacity',
  'points',
  'r',
  'rx',
  'ry',
  'stroke',
  'strokeLinecap',
  'strokeLinejoin',
  'strokeMiterlimit',
  'strokeWidth',
  'transform',
  'width',
  'x',
  'x1',
  'x2',
  'y',
  'y1',
  'y2',
] as const)

/**
 * Attributes a shape may carry that a CONTAINER may not.
 *
 * The reason for both is flattening. Paint inherits, so a group's `fill` can be
 * pushed down onto its children unchanged and the group then dropped. These two
 * do not inherit, and copying them onto each child would change the drawing:
 *
 * - `transform` COMPOSES rather than inherits, and expressing a group transform
 *   on each child means multiplying matrices.
 * - `opacity` applies to the group AS A GROUP — it composites the children
 *   together first and fades the result once. Copied onto each child it fades
 *   them separately, so anywhere two of them overlap the seam shows through.
 *
 * Refusing both keeps the flattening step honest: everything the parser drops is
 * something it has proved it can drop.
 */
export const TWEMOJI_SHAPE_ONLY_ATTRS = Object.freeze(['opacity', 'transform'] as const)

/**
 * Upstream draws every emoji on a 36 x 36 grid. Asserted against each vendored
 * file by the parser, so a file drawn on a different grid fails loudly rather
 * than being served at the wrong intrinsic size — which, on an `<img>` carrying
 * `width` and `height`, is a layout shift rather than a visible mistake.
 */
export const TWEMOJI_GRID = 36
export const TWEMOJI_VIEWBOX = `0 0 ${String(TWEMOJI_GRID)} ${String(TWEMOJI_GRID)}`
